import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import CompressRoundedIcon from "@mui/icons-material/CompressRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function CompressorVsLimiterLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/compressor-vs-limiter";
  const pageTitle = "Compressor vs Limiter: What Is the Difference?";
  const pageDescription =
    "Learn the difference between a compressor and limiter, how each controls dynamics, when to use compression, when to use limiting, and how they work together in mixing and mastering.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "compressor vs limiter",
          "difference between compressor and limiter",
          "compression vs limiting",
          "when to use a compressor",
          "when to use a limiter",
          "limiter vs compressor mastering",
          "audio dynamics processors",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Audio compression" },
          { "@type": "Thing", name: "Limiter" },
          { "@type": "Thing", name: "Dynamic range" },
          { "@type": "Thing", name: "Mixing" },
          { "@type": "Thing", name: "Mastering" },
          { "@type": "Thing", name: "Music production" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Compressor vs Limiter",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What they share",
      text: "Both compressors and limiters are dynamics processors that reduce level once a signal crosses a threshold.",
      icon: <CompareArrowsRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Main difference",
      text: "A compressor usually works more gradually and flexibly, while a limiter is more aggressive and focused on strict peak control.",
      icon: <SpeedRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where they are used",
      text: "Vocals, drums, bass, guitars, buses, mastering chains, broadcast audio, and nearly every modern production workflow.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const comparisonPoints = [
    {
      name: "Compression",
      description:
        "Compression is usually used to shape dynamic range in a more controlled, musical, and flexible way. It can smooth a vocal, glue a drum bus, thicken a bass, or help a mix feel more cohesive.",
    },
    {
      name: "Limiting",
      description:
        "Limiting is usually used when stronger peak control is needed. It is especially common at the end of a chain where the goal is to stop overs, control transients, or set a hard output ceiling.",
    },
    {
      name: "Ratio Behavior",
      description:
        "Compressors often use moderate ratios, while limiters generally use very high ratios so signals above threshold are held down much more strictly.",
    },
    {
      name: "Typical Result",
      description:
        "Compression often changes feel, density, and movement. Limiting often changes maximum level, peak behavior, and final loudness more directly.",
    },
  ];

  const controlDifferences = [
    {
      name: "Threshold",
      description:
        "Both tools use threshold, but with a limiter the threshold often leads to more immediate and more obvious peak restraint.",
    },
    {
      name: "Ratio",
      description:
        "Compressors may use gentle or moderate ratios such as 2:1, 4:1, or 8:1. Limiters usually operate at much higher ratios, sometimes effectively acting like a ceiling.",
    },
    {
      name: "Attack and Release",
      description:
        "Compressors often use attack and release for groove, punch, and musical movement. Limiters often use faster behavior to catch peaks more strictly.",
    },
    {
      name: "Ceiling / Output",
      description:
        "Limiters often include an explicit output ceiling to stop the signal from going above a set level, especially in mastering.",
    },
  ];

  const compressorUses = [
    "Smoothing out a vocal performance",
    "Adding consistency to bass guitar",
    "Giving drums more glue or sustain",
    "Controlling dynamic swings on acoustic instruments",
    "Making a bus feel tighter and more cohesive",
    "Shaping groove and movement with attack and release timing",
  ];

  const limiterUses = [
    "Catching peaks on a mix bus or master bus",
    "Preventing digital clipping",
    "Raising final loudness in mastering",
    "Controlling occasional transient spikes",
    "Protecting broadcast or live audio chains from overload",
    "Setting a strict output ceiling before export",
  ];

  const bestPractices = [
    "Use a compressor when you want to shape feel, movement, consistency, or sustain more musically.",
    "Use a limiter when you need stronger peak control or want to keep the output under a defined ceiling.",
    "Do not use a limiter to fix a mix that really needs balancing, EQ, or compression earlier in the chain.",
    "Watch for over-processing. Too much compression can kill life, and too much limiting can kill punch.",
    "Often the best result is using both: compression first for control and tone, then limiting later for final peak management.",
  ];

  const relatedLinks = [
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Learn how compressors reduce dynamic range and shape tone, movement, and consistency.",
    },
    {
      title: "Limiting",
      to: "/learning/limiting",
      description:
        "Explore how limiters control peaks, prevent clipping, and manage final loudness.",
    },
    {
      title: "Mastering Basics",
      to: "/learning/mastering-basics",
      description:
        "See how compressors and limiters often work together in a mastering chain.",
    },
    {
      title: "Transient Shaper",
      to: "/learning/transient-shaper",
      description:
        "Understand how transient shaping differs from both compression and limiting when the goal is punch and envelope control.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Compressor vs Limiter | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Compressor vs Limiter
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Compressors and limiters belong to the same general family of
                      audio processors. Both are designed to control dynamics by
                      reducing level when a signal goes above a threshold. Because of
                      that, people often describe a limiter as a more extreme form of
                      compression.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      That description is useful, but it is not the whole story.
                      Compressors are usually used for musical control, tone shaping,
                      movement, and consistency. Limiters are usually used for strict
                      peak control, preventing clipping, and setting final loudness.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What Compressors and Limiters Have in Common
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Both processors react when a signal crosses a threshold. Once
                    that happens, they reduce gain. This lowers dynamic range and
                    helps manage how loud the signal becomes. Both can influence
                    punch, consistency, density, and perceived loudness.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    In many plugin lineups, you will see similar controls on both
                    types of processors. Threshold, attack, release, output level,
                    and gain reduction meters are all common. This overlap is one
                    reason the two are easy to confuse at first.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    The most important difference is not whether they reduce gain.
                    Both do that. The difference is how strongly they do it and what
                    role they usually play in a signal chain.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      The Main Difference
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {comparisonPoints.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Typography sx={{ color: accent, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A good way to think about it is this: a compressor usually helps
                    shape the behavior of the signal, while a limiter usually helps
                    stop the signal from going too far.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      How Their Controls Tend to Differ
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {controlDifferences.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CompressRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        When to Use a Compressor
                      </Typography>
                    </Stack>

                    <Stack spacing={1.1}>
                      {compressorUses.map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <CompressRoundedIcon
                            sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Compression is often the better choice when you want to shape
                      how a source behaves over time. It can smooth level changes,
                      control sustain, add density, and even influence groove depending
                      on how attack and release are set.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SpeedRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        When to Use a Limiter
                      </Typography>
                    </Stack>

                    <Stack spacing={1.1}>
                      {limiterUses.map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <SpeedRoundedIcon
                            sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Limiting is usually the better choice when you need stronger
                      control over maximum level. It is especially useful near the end
                      of a chain, where preventing clipping or managing final loudness
                      becomes more important than shaping musical dynamics in a subtle
                      way.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Do They Replace Each Other?
                    </Typography>
                  </Stack>

                  <Paper
                    elevation={0}
                    sx={{
                      ...subtlePanelSx,
                      p: 1.5,
                    }}
                  >
                    <Stack spacing={1.2}>
                      <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                        Usually no
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        In some cases, a limiter can be used where a compressor might
                        otherwise go, or a compressor can be set aggressively enough
                        to approach limiter-like behavior. But in real-world
                        workflows, they often serve different purposes rather than
                        replacing each other.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        A common approach is to use compression earlier for control,
                        tone, and consistency, then use limiting later for final peak
                        management. That combination is especially common on buses and
                        mastering chains.
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarningAmberRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Common Mistakes
                      </Typography>
                    </Stack>

                    <Stack spacing={1.15}>
                      {[
                        "Using a limiter when the real goal is musical consistency, which a compressor often handles better.",
                        "Using compression too heavily when a simple peak-catching limiter would solve the problem more cleanly.",
                        "Trying to get competitive loudness with only compression instead of using compression and limiting together appropriately.",
                        "Over-processing until the track loses punch, movement, and natural energy.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <WarningAmberRoundedIcon
                            sx={{ color: accent, mt: "2px", flexShrink: 0, fontSize: 20 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Best Practices
                      </Typography>
                    </Stack>

                    <Stack spacing={1.15}>
                      {bestPractices.map((item, index) => (
                        <Stack key={index} direction="row" spacing={1.1} alignItems="flex-start">
                          <TipsAndUpdatesRoundedIcon
                            sx={{ color: accent, mt: "2px", flexShrink: 0, fontSize: 20 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GraphicEqRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Final Thoughts
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Compressors and limiters are closely related, but they are not
                    the same tool with different labels. Compression is usually the
                    more flexible and musical tool for shaping consistency, movement,
                    and feel. Limiting is usually the stricter tool for controlling
                    peaks and setting boundaries.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    The best choice depends on what problem you are trying to solve.
                    In many cases, the strongest workflow is not compressor or
                    limiter. It is compressor and limiter, each doing the job it is
                    best suited for.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Related Learning Topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Paper
                        key={item.title}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          minHeight: "75%",
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}
                          >
                            {item.description}
                          </Typography>
                        </Box>

                        <Box>
                          <Button
                            component={RouterLink}
                            to={item.to}
                            sx={{
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              backgroundColor: "rgba(255,255,255,0.05)",
                              border: `1px solid ${accent}`,
                              "&:hover": {
                                borderColor: style.color4,
                                backgroundColor: "rgba(255,255,255,0.10)",
                              },
                            }}
                          >
                            Read More
                          </Button>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * Parish Audio is not affiliated with any brands, products, or artists
                  mentioned for educational context on this page. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}